import './ProductPrice.scss';

import { registryComponent } from '@boost-sd/components-registry/registry';
import { useVDomToString } from '@boost-sd/core-js';
import type { ReactNodeRenderer } from '@components/CustomizableNode';
import CustomizableNode from '@components/CustomizableNode';
import FormatCurrency from '@components/FormatCurrency';
import { createClsNameMap, mergeModifiers } from '@utils';
import { useMemo } from 'react';

import TranslateWithComponent from './TranslateWithComponent';

export const clsNameMap = createClsNameMap({
  elements: {
    compare: createClsNameMap(),
    wrapper: createClsNameMap(),
    content: createClsNameMap({
      modifiers: [
        'row-reverse',
        'column-reverse',
        'column',
        'flex-wrap',
        'text-align-left',
        'text-align-right',
        'text-align-center',
      ],
    }),
    saving: createClsNameMap(),
  },
})('product-price');

export type ProductPriceSetting = {
  showCentAsSuperscript?: boolean;
  showCurrencyCodes?: boolean;
  showMultiVariantPrice?: string;

  compareAtPricePosition?: 'top' | 'left' | 'right' | 'bottom';

  showSavingDisplay?: boolean;
  savingDisplayColor?: string;

  priceStyle?: Record<string, unknown>;
  salePriceStyle?: Record<string, unknown>;
  comparePriceStyle?: Record<string, unknown>;
  savingStyle?: Record<string, unknown>;
  textAlign?: 'left' | 'right' | 'center';
};

export type ProductPriceData = {
  isSale: boolean;
  salePercent: string | undefined;
  saleAmount: number | undefined;
  price_min: number;
  price_max: number;
  compare_at_price_max: number | null;
  compare_at_price_min: number | null;
};

export type ProductPriceProps = {
  setting: ProductPriceSetting;
  data: ProductPriceData;
  onRender?: ReactNodeRenderer;
};

const ProductPrice = (props: ProductPriceProps) => {
  const { setting, data, onRender } = props;

  const {
    showMultiVariantPrice,
    showCurrencyCodes,
    showCentAsSuperscript,
    compareAtPricePosition,

    // Style
    priceStyle,
    salePriceStyle,
    comparePriceStyle,
    savingStyle,

    // Saving Display
    showSavingDisplay,

    // Text align
    textAlign,
  } = setting;

  const {
    isSale,
    salePercent,
    saleAmount,
    price_min,
    price_max,
    compare_at_price_max,
    compare_at_price_min,
  } = data;

  const minPriceString = useVDomToString({
    vDOM: (
      <FormatCurrency
        value={price_min}
        formatWithCurrency={showCurrencyCodes}
        showCentAsSuperscript={showCentAsSuperscript}
        removeDecimalPoint={showCentAsSuperscript}
      />
    ),
  });

  const maxPriceString = useVDomToString({
    vDOM: (
      <FormatCurrency
        value={price_max}
        formatWithCurrency={showCurrencyCodes}
        showCentAsSuperscript={showCentAsSuperscript}
        removeDecimalPoint={showCentAsSuperscript}
      />
    ),
  });

  const saleLabelString = useVDomToString({ vDOM: <FormatCurrency value={saleAmount} /> });

  const compareAtPrice = (value?: number | null) => {
    if (!value || !isSale) return <></>;

    return (
      <FormatCurrency
        isPriceCompare
        style={comparePriceStyle}
        value={value}
        formatWithCurrency={showCurrencyCodes}
        showCentAsSuperscript={showCentAsSuperscript}
        removeDecimalPoint={showCentAsSuperscript}
      />
    );
  };

  const renderSaving = (style: Record<string, unknown>) => {
    if (!showSavingDisplay || !saleAmount) return <></>;

    return (
      <TranslateWithComponent
        style={{
          ...style,
          ...savingStyle,
        }}
        className={clsNameMap.elm('saving')}
        translation='productItem.savingAmount'
        data={{
          salePercent,
          saleAmount: saleLabelString,
        }}
      />
    );
  };

  const mainPrice = (value: number, valueCompare?: number | null, valueMax?: number) => {
    const style = (isSale ? salePriceStyle : priceStyle) || {};

    if (compareAtPricePosition === 'top' || compareAtPricePosition === 'bottom') {
      return (
        <div
          className={mergeModifiers(clsNameMap['content'], [
            {
              'column-reverse': compareAtPricePosition === 'top',
              column: compareAtPricePosition === 'bottom',
            },
            textAlign && [`text-align-${textAlign}`],
          ])}
        >
          <div
            className={mergeModifiers(clsNameMap.content, [
              textAlign && [`text-align-${textAlign}`],
            ])}
          >
            {valueMax ? (
              <TranslateWithComponent
                style={style}
                translation='productItem.amount'
                data={{
                  minPrice: minPriceString,
                  maxPrice: maxPriceString,
                }}
              />
            ) : (
              <FormatCurrency
                style={style}
                value={value}
                formatWithCurrency={showCurrencyCodes}
                showCentAsSuperscript={showCentAsSuperscript}
                removeDecimalPoint={showCentAsSuperscript}
              />
            )}
            {renderSaving(style)}
          </div>

          {valueCompare ? (
            <div className={clsNameMap.elm('compare')}>{compareAtPrice(valueCompare)}</div>
          ) : (
            <></>
          )}
        </div>
      );
    }

    return (
      <div className={clsNameMap.elm('wrapper')}>
        <span
          className={mergeModifiers(clsNameMap['content'], [
            {
              'row-reverse': compareAtPricePosition === 'left',
              [`text-align-${textAlign}`]: true,
            },
            textAlign && [`text-align-${textAlign}`],
          ])}
        >
          {valueMax ? (
            <TranslateWithComponent
              style={style}
              translation='productItem.amount'
              data={{
                minPrice: minPriceString,
                maxPrice: maxPriceString,
              }}
            />
          ) : (
            <FormatCurrency
              style={style}
              value={value}
              formatWithCurrency={showCurrencyCodes}
              showCentAsSuperscript={showCentAsSuperscript}
              removeDecimalPoint={showCentAsSuperscript}
            />
          )}
          {compareAtPrice(valueCompare)}
        </span>

        {renderSaving(style)}
      </div>
    );
  };

  const renderPrice = useMemo(() => {
    /** Check if min-max is same value, show one price */
    const isSamePrice =
      `${price_min}-${compare_at_price_min}` === `${price_max}-${compare_at_price_max}`;

    if (isSamePrice || showMultiVariantPrice === 'none')
      return mainPrice(price_min, compare_at_price_min);

    return mainPrice(price_min, compare_at_price_min, price_max);
  }, [data, mainPrice, showMultiVariantPrice]);

  return (
    <div className={clsNameMap.root()}>
      {/* TODO: Created PFSN-48716 - format price by lang/location */}
      <CustomizableNode renderer={onRender}>{renderPrice}</CustomizableNode>
    </div>
  );
};

export default registryComponent('ProductPrice', ProductPrice);
